import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, ShieldCheck } from "lucide-react";
import { EASE } from "./Reveal";
import { HERO_STATS } from "../content";
import { scrollToId } from "./SmoothScroll";

const LINES = [
  { t: "Leadership", cls: "" },
  { t: "is plucked,", cls: "italic text-emerald-400" },
  { t: "never posted.", cls: "" },
];

const HeroFrame = ({ scrollY }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });
  const imgY = useTransform(scrollY, [0, 1], ["0%", "16%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <div className="pointer-events-none absolute -inset-10 rounded-full bg-emerald-500/20 blur-[90px]" />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
          className="relative aspect-[4/5] w-full overflow-hidden rounded-t-[999px] rounded-b-md border border-white/10"
        >
          <motion.img
            src="/images/boardroom.jpg"
            alt="Quiet executive boardroom with a single shaft of light"
            style={{ y: imgY }}
            className="absolute inset-0 h-[118%] w-full -top-[9%] object-cover grayscale-[35%] contrast-[1.1]"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c0e] via-[#0a0c0e]/20 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(201,161,59,0.18),transparent_60%)] mix-blend-screen" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          style={{ transform: "translateZ(60px)" }}
          className="absolute -left-4 sm:-left-10 bottom-10 w-[78%] max-w-[300px] rounded-md border border-white/15 bg-[#0a0c0e]/60 p-5 backdrop-blur-xl"
          data-testid="hero-glass-card"
        >
          <div className="flex items-center gap-2 text-emerald-300">
            <ShieldCheck className="h-4 w-4" />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase">Retained search</span>
          </div>
          <p className="font-display mt-3 text-2xl leading-tight">CXO mandates handled in strict confidence.</p>
          <p className="mt-2 text-xs text-zinc-400">Banking · NBFC · Insurance · Payments · Fintech</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden pt-28 sm:pt-32 pb-16" data-testid="hero-section">
      <div className="hairline-grid pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7 flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="eyebrow flex items-center gap-3"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            Executive search · BFSI & Startups
          </motion.p>
          <h1 className="font-display mt-8 text-[3.4rem] leading-[0.92] sm:text-7xl lg:text-[7.4rem] font-medium tracking-[-0.02em]" data-testid="hero-headline">
            {LINES.map((l, i) => (
              <span key={l.t} className="block overflow-hidden pb-2">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {l.t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-zinc-300"
          >
            PLUK HR is the executive search vertical of Anweta Ventures. We find, assess and help retain CXO, senior and
            mid-management leaders for banks, financial institutions, fintechs and high-growth startups.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("mandate")}
              data-testid="hero-cta-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-emerald-500 pl-6 pr-2 py-2 text-[15px] font-medium text-[#0a0c0e] transition-colors hover:bg-emerald-400"
            >
              Share a hiring mandate
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[#0a0c0e] text-emerald-400 transition-transform duration-500 group-hover:rotate-45">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </button>
            <button
              onClick={() => scrollToId("process")}
              data-testid="hero-secondary-btn"
              className="group inline-flex items-center gap-2 px-2 py-3 text-[15px] text-zinc-200 hover:text-white"
            >
              How we search
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 lg:pl-6 flex items-center">
          <div className="w-full max-w-[460px] mx-auto lg:mx-0 lg:ml-auto">
            <HeroFrame scrollY={scrollYProgress} />
          </div>
        </div>
      </div>
      <div className="relative mx-auto mt-20 max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-white/10">
          {HERO_STATS.map((s, i) => (
            <motion.div
              key={s.k}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 + i * 0.08, ease: EASE }}
              className={`py-6 pr-4 border-white/10 ${i > 0 ? "border-t sm:border-t-0 sm:border-l sm:pl-6" : ""}`}
              data-testid={`hero-stat-${i}`}
            >
              <p className="font-display text-4xl sm:text-5xl text-[#f1f0ea]">{s.k}</p>
              <p className="mt-2 text-sm text-zinc-400 max-w-[220px]">{s.v}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
